import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, ScanLine, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { lookupBarcode } from "../lib/api";
import { cn } from "../lib/utils";

const SUPPORTED_FORMATS = [
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "code_128",
  "code_39",
];

// html5-qrcode constants — match the format codes used internally so the
// engine restricts itself to retail barcodes (no QR/PDF417 flapping).
const HTML5_QRCODE_FORMATS_LAZY = async () => {
  const { Html5QrcodeSupportedFormats } = await import("html5-qrcode");
  return [
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODE_39,
  ];
};

const READER_DOM_ID = "blendsafe-html5qr-reader";
const SCAN_TIMEOUT_MS = 15000;

export default function BarcodeScanner({ open, onOpenChange, onMatch }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(0);
  const html5Ref = useRef(null);     // Html5Qrcode instance (fallback engine)
  const timeoutRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  // engine = "native" (BarcodeDetector) | "html5qr" (html5-qrcode lib) | null
  const [engine, setEngine] = useState(null);

  useEffect(() => {
    if (!open) {
      stopScan();
      return;
    }
    beginScanning();
    return stopScan;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function clearScanTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  function armScanTimeout() {
    clearScanTimeout();
    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
      stopScan();
    }, SCAN_TIMEOUT_MS);
  }

  function beginScanning() {
    setTimedOut(false);
    const hasNative = typeof window !== "undefined" && "BarcodeDetector" in window;
    if (hasNative) {
      setSupported(true);
      setEngine("native");
      startScan().then(() => armScanTimeout()).catch((e) => {
        console.warn("native scan start failed", e);
        setEngine("html5qr");
        startHtml5Scan().then(() => armScanTimeout()).catch(() => setSupported(false));
      });
    } else {
      setEngine("html5qr");
      startHtml5Scan().then(() => armScanTimeout()).catch(() => setSupported(false));
    }
  }

  function handleRetry() {
    setTimedOut(false);
    stopScan();
    // small tick so the reader DOM node remounts cleanly before html5-qrcode attaches.
    setTimeout(beginScanning, 50);
  }

  async function startScan() {
    try {
      detectorRef.current = new window.BarcodeDetector({
        formats: SUPPORTED_FORMATS,
      });
    } catch {
      setSupported(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      tick();
    } catch (e) {
      toast.error("Camera permission denied", {
        description: "Use the manual entry below instead.",
      });
      setSupported(false);
      throw e;
    }
  }

  async function startHtml5Scan() {
    const { Html5Qrcode } = await import("html5-qrcode");
    const formats = await HTML5_QRCODE_FORMATS_LAZY();
    // Wait one tick so the container DOM node is mounted.
    await new Promise((r) => setTimeout(r, 50));
    if (!document.getElementById(READER_DOM_ID)) {
      // Container disappeared (modal closed during dynamic import) — abort.
      return;
    }
    try {
      const inst = new Html5Qrcode(READER_DOM_ID, { formatsToSupport: formats });
      html5Ref.current = inst;
      await inst.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 140 } },
        async (decodedText) => {
          if (!decodedText) return;
          // Stop immediately so we don't fire multiple lookups
          await stopHtml5Scan();
          await handleCode(decodedText);
        },
        () => { /* noisy frame errors — ignore */ },
      );
      setScanning(true);
    } catch (e) {
      console.warn("html5-qrcode start failed", e);
      toast.error("Camera permission denied", {
        description: "Use the manual entry below instead.",
      });
      setSupported(false);
      throw e;
    }
  }

  function stopScan() {
    setScanning(false);
    clearScanTimeout();
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    stopHtml5Scan();
  }

  async function stopHtml5Scan() {
    const inst = html5Ref.current;
    if (!inst) return;
    try {
      const state = inst.getState && inst.getState();
      // 2 = SCANNING in html5-qrcode enum
      if (state === 2) await inst.stop();
      await inst.clear();
    } catch { /* ignore — best-effort cleanup */ }
    html5Ref.current = null;
  }

  async function tick() {
    if (!videoRef.current || !detectorRef.current) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    try {
      const results = await detectorRef.current.detect(videoRef.current);
      if (results && results.length > 0) {
        const code = results[0].rawValue;
        if (code) {
          stopScan();
          await handleCode(code);
          return;
        }
      }
    } catch {
      // detection errors are noisy; ignore and keep scanning
    }
    rafRef.current = requestAnimationFrame(tick);
  }

  async function handleCode(code) {
    setBusy(true);
    try {
      const data = await lookupBarcode(code);
      onMatch?.(data);
      onOpenChange(false);
      toast.success(`Found: ${data.name}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Barcode not found";
      toast.error(detail, {
        description: "Try the manual ingredient list instead.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="barcode-scanner-dialog"
        className="bg-cream-50 border-surface-line rounded-3xl max-w-md p-7 sm:p-8"
      >
        <DialogTitle className="text-[26px] font-extrabold tracking-tight text-ink leading-tight">
          Scan a product
        </DialogTitle>
        <DialogDescription className="text-ink-soft/85 mt-2 text-[14px]">
          Hold a UPC or EAN barcode steady inside the frame. We'll pull the
          ingredient list from Open Beauty Facts.
        </DialogDescription>

        <div className="mt-5">
          {timedOut ? (
            <div
              data-testid="barcode-timeout-panel"
              className="rounded-2xl border border-caution-border bg-caution-bg p-4 space-y-3"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-caution-deep mt-0.5" />
                <div className="text-[13px] text-caution-deep leading-snug">
                  Couldn't detect a barcode. Try better lighting, hold steady, or
                  enter the number manually below.
                </div>
              </div>
              <Button
                type="button"
                data-testid="barcode-retry-btn"
                onClick={handleRetry}
                className="h-11 rounded-xl bg-ink text-cream-50 hover:bg-ink-soft w-full"
              >
                <ScanLine className="w-4 h-4 mr-2" /> Try again
              </Button>
            </div>
          ) : supported ? (
            engine === "native" ? (
              <div
                className={cn(
                  "relative aspect-[4/3] rounded-2xl overflow-hidden bg-black border border-surface-line",
                )}
              >
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-6 border-2 border-white/70 rounded-2xl pointer-events-none">
                  <div className="absolute -top-px left-0 right-0 h-[2px] bg-safe-solid animate-pulse" />
                </div>
                {busy && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
            ) : (
              // html5-qrcode fallback — the lib injects its own <video>
              <div
                className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-black border border-surface-line"
                data-testid="barcode-html5-container"
              >
                <div id={READER_DOM_ID} className="absolute inset-0" />
                <div className="absolute inset-6 border-2 border-white/70 rounded-2xl pointer-events-none">
                  <div className="absolute -top-px left-0 right-0 h-[2px] bg-safe-solid animate-pulse" />
                </div>
                {busy && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="rounded-2xl border border-caution-border bg-caution-bg p-4 flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-caution-deep mt-0.5" />
              <div className="text-[13px] text-caution-deep leading-snug">
                Live scanning isn't available in this browser. Type the barcode
                number from the package below — it works just as well.
              </div>
            </div>
          )}
        </div>

        {/* Manual entry — always available */}
        <div className="mt-5 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink-mute">
            Or enter barcode manually
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="barcode-manual-input"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g, ""))}
              placeholder="3600523715169"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="off"
              className="h-11"
            />
            <Button
              data-testid="barcode-manual-submit"
              onClick={() => manualCode && handleCode(manualCode)}
              disabled={busy || !manualCode}
              className="h-11 rounded-xl bg-ink text-cream-50 hover:bg-ink-soft"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Look up"}
            </Button>
          </div>
        </div>

        <div className="mt-4 text-[11px] text-ink-mute leading-relaxed">
          Source: Open Beauty Facts — a community database. Coverage varies by
          country and brand.
        </div>
      </DialogContent>
    </Dialog>
  );
}
